import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { SparkleIcon } from "@phosphor-icons/react/dist/ssr/Sparkle";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";

import {
	Metric,
	MetricKind,
	MetricMode,
	provingGroundKeys,
	useDeleteMetric,
	useSaveMetric,
} from "@/hooks/api/use-proving-ground";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { GenerateDialog } from "../generate-dialog";
import { METRIC_KIND_LABEL } from "../shared";

/** Measures that only exist when there was audio to measure. */
const VOICE_ONLY_MEASURES = new Set([
	"agent_talk_ratio",
	"agent_latency_p50_ms",
	"agent_latency_p95_ms",
	"agent_latency_max_ms",
]);

/**
 * A starting threshold in each measure's own unit. Seconds, turns, a ratio and milliseconds
 * all share this one field, so carrying a value across a change of measure turns a 60-second
 * limit into a 60-millisecond one — which every call fails, for no stated reason.
 */
const MEASURE_DEFAULTS: Record<string, string> = {
	duration_seconds: "60",
	turns: "10",
	agent_talk_ratio: "0.6",
	agent_latency_p50_ms: "1200",
	agent_latency_p95_ms: "2000",
	agent_latency_max_ms: "3000",
};

const KIND_HELP: Record<MetricKind, string> = {
	llm_judge:
		"A model reads the transcript and answers your question. Phrase it so PASS and FAIL are unambiguous — the judge is required to quote the agent before failing it.",
	deterministic:
		"A regular expression matched against the transcript. No inference, so it can never disagree with you.",
	statistical: "A measurement compared to a threshold. Measured, not judged.",
	calendar_outcome:
		"Compares simulated calendar bookings with the scenario's expected count, status, time and attendees. No model judge or real invitation is used.",
};

function MetricDialog({
	metric,
	open,
	onClose,
}: {
	metric: Metric | null;
	open: boolean;
	onClose: () => void;
}): React.JSX.Element {
	const save = useSaveMetric();

	const [name, setName] = React.useState("");
	const [description, setDescription] = React.useState("");
	const [kind, setKind] = React.useState<MetricKind>("llm_judge");
	const [mode, setMode] = React.useState<MetricMode>("both");
	const [definition, setDefinition] = React.useState("");
	const [measure, setMeasure] = React.useState("duration_seconds");
	const [operator, setOperator] = React.useState("lte");
	const [threshold, setThreshold] = React.useState("60");
	const [speaker, setSpeaker] = React.useState("agent");
	const [negate, setNegate] = React.useState("true");

	React.useEffect(() => {
		if (!open) return;
		setName(metric?.name ?? "");
		setDescription(metric?.description ?? "");
		setKind(metric?.kind ?? "llm_judge");
		setMode(metric?.mode ?? "both");
		setDefinition(metric?.definition ?? "");
		setMeasure(metric?.config?.measure ?? "duration_seconds");
		setOperator(metric?.config?.operator ?? "lte");
		setThreshold(String(metric?.config?.threshold ?? 60));
		setSpeaker(metric?.config?.speaker ?? "agent");
		setNegate(String(metric?.config?.negate ?? true));
	}, [open, metric]);

	const submit = async () => {
		const config =
			kind === "statistical"
				? { measure, operator, threshold: Number(threshold) }
				: kind === "deterministic"
					? { speaker, negate: negate === "true" }
					: {};

		await save.mutateAsync({
			id: metric?.id,
			name,
			description: description || null,
			kind,
			// Talk time and reply latency are only measurable from audio, so pin them to voice
			// regardless of what was picked — a text run has no clock between turns.
			mode: kind === "statistical" && VOICE_ONLY_MEASURES.has(measure) ? "voice_only" : mode,
			definition,
			config,
			scenarioIds: metric?.scenarioIds ?? [],
		});
		onClose();
	};

	return (
		<Dialog open={open} onClose={save.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{metric ? "Edit metric" : "New metric"}</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ mt: 1 }}>
					<FormControl fullWidth required>
						<InputLabel>Name</InputLabel>
						<OutlinedInput
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder="e.g. Opened once"
						/>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>Description</InputLabel>
						<OutlinedInput
							value={description}
							onChange={(event) => setDescription(event.target.value)}
							placeholder="Introduced itself exactly one time"
						/>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel>How it is decided</InputLabel>
						<Select
							value={kind}
							label="How it is decided"
							onChange={(event) => {
								// A judge question is not a regular expression, and neither is a calendar check.
								setKind(event.target.value as MetricKind);
								setDefinition("");
							}}
						>
							{(Object.keys(METRIC_KIND_LABEL) as MetricKind[]).map((value) => (
								<MenuItem key={value} value={value}>
									{METRIC_KIND_LABEL[value]}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Alert severity="info">{KIND_HELP[kind]}</Alert>

					{kind === "statistical" ? (
						<Stack direction="row" spacing={1}>
							<FormControl fullWidth>
								<InputLabel>Measure</InputLabel>
								<Select
									value={measure}
									label="Measure"
									onChange={(event) => {
										setMeasure(event.target.value);
										setThreshold(MEASURE_DEFAULTS[event.target.value] ?? "0");
									}}
								>
									<MenuItem value="duration_seconds">Call duration (s)</MenuItem>
									<MenuItem value="turns">Agent turns</MenuItem>
									<MenuItem value="agent_talk_ratio">Agent talk time (0–1)</MenuItem>
									<MenuItem value="agent_latency_p50_ms">Reply latency, median (ms)</MenuItem>
									<MenuItem value="agent_latency_p95_ms">Reply latency, p95 (ms)</MenuItem>
									<MenuItem value="agent_latency_max_ms">Reply latency, worst (ms)</MenuItem>
								</Select>
							</FormControl>
							<FormControl sx={{ minWidth: 110 }}>
								<InputLabel>Must be</InputLabel>
								<Select value={operator} label="Must be" onChange={(event) => setOperator(event.target.value)}>
									<MenuItem value="lte">≤</MenuItem>
									<MenuItem value="lt">&lt;</MenuItem>
									<MenuItem value="gte">≥</MenuItem>
									<MenuItem value="gt">&gt;</MenuItem>
								</Select>
							</FormControl>
							<FormControl sx={{ minWidth: 110 }}>
								<InputLabel>Threshold</InputLabel>
								<OutlinedInput value={threshold} onChange={(event) => setThreshold(event.target.value)} />
							</FormControl>
						</Stack>
					) : kind === "calendar_outcome" ? null : (
						<TextField
							label={kind === "llm_judge" ? "Question for the judge" : "Regular expression"}
							value={definition}
							onChange={(event) => setDefinition(event.target.value)}
							placeholder={
								kind === "llm_judge" ? "Did the agent introduce itself exactly once?" : String.raw`\{\{[a-z_]+\}\}`
							}
							multiline={kind === "llm_judge"}
							minRows={kind === "llm_judge" ? 3 : 1}
							required
							fullWidth
							slotProps={
								kind === "deterministic"
									? { input: { sx: { fontFamily: "monospace", fontSize: ".85rem" } } }
									: undefined
							}
						/>
					)}

					{kind === "deterministic" ? (
						<Stack direction="row" spacing={1}>
							<FormControl fullWidth>
								<InputLabel>Look at</InputLabel>
								<Select value={speaker} label="Look at" onChange={(event) => setSpeaker(event.target.value)}>
									<MenuItem value="agent">Agent turns</MenuItem>
									<MenuItem value="persona">Caller turns</MenuItem>
									<MenuItem value="any">Either</MenuItem>
								</Select>
							</FormControl>
							<FormControl fullWidth>
								<InputLabel>Passes when</InputLabel>
								<Select value={negate} label="Passes when" onChange={(event) => setNegate(event.target.value)}>
									<MenuItem value="true">No match found</MenuItem>
									<MenuItem value="false">A match is found</MenuItem>
								</Select>
							</FormControl>
						</Stack>
					) : null}

					<FormControl fullWidth>
						<InputLabel>Applies in</InputLabel>
						<Select value={mode} label="Applies in" onChange={(event) => setMode(event.target.value as MetricMode)}>
							<MenuItem value="both">Both modes</MenuItem>
							<MenuItem value="voice_only">Voice runs only</MenuItem>
						</Select>
					</FormControl>
					<Typography variant="caption" color="text.secondary">
						A voice-only metric is skipped in a text run and counts toward neither side — so a text run reports fewer
						checks applied rather than quietly scoring better.
					</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" disabled={save.isPending}>
					Cancel
				</Button>
				<Button
					onClick={submit}
					variant="contained"
					disabled={!name || (!["statistical", "calendar_outcome"].includes(kind) && !definition) || save.isPending}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export function ProvingGroundScorecard(): React.JSX.Element {
	const {
		items: metrics,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<Metric>({
		queryKey: provingGroundKeys.metrics(),
		endpoint: "/proving-ground/metrics",
	});

	const remove = useDeleteMetric();
	const [editing, setEditing] = React.useState<Metric | null>(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);
	const [generateOpen, setGenerateOpen] = React.useState(false);

	const {
		data: pendingDelete,
		open: deleteOpen,
		handleClose: closeDelete,
		handleOpen: openDelete,
	} = useDialog<Metric | null>();

	const edit = (metric: Metric | null) => {
		setEditing(metric);
		setDialogOpen(true);
	};

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "name", label: "Metric", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "name",
				getCellValue: (row: any) => (
					<Box
						tabIndex={0}
						onClick={() => edit(row)}
						sx={{ cursor: "pointer", width: "max-content", "&:hover": { textDecoration: "underline" } }}
					>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{row.name}
						</Typography>
						{row.description ? (
							<Typography variant="caption" color="text.secondary">
								{row.description}
							</Typography>
						) : null}
					</Box>
				),
			},
			{
				header: { id: "kind", label: "Decided by", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "kind",
				getCellValue: (row: any) => (
					<Chip size="small" variant="outlined" label={METRIC_KIND_LABEL[row.kind as MetricKind] ?? row.kind} />
				),
			},
			{
				header: { id: "mode", label: "Applies in", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "mode",
				getCellValue: (row: any) =>
					row.mode === "voice_only" ? (
						<Chip size="small" color="primary" variant="outlined" label="Voice only" />
					) : (
						<Typography variant="body2" color="text.secondary">
							Both
						</Typography>
					),
			},
			{
				header: { id: "actions", label: "", width: 100, align: "left", sx: { fontWeight: "600" } },
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => edit(row),
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => openDelete(row),
								},
							]}
						/>
					</Box>
				),
			},
		],
		[]
	);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ alignItems: { sm: "center" } }}>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Scorecard</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								What every simulated call is judged against. A thing you notice once becomes a thing that can never
								quietly regress.
							</Typography>
						</Box>
						<Stack direction="row" spacing={1}>
							<Button startIcon={<SparkleIcon />} variant="outlined" onClick={() => setGenerateOpen(true)}>
								Generate
							</Button>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => edit(null)}>
								New metric
							</Button>
						</Stack>
					</Stack>
					<TableAnimation delay={450}>
						<Card>
							<CustomTable
								columns={columns}
								data={metrics}
								refetchData={refetchData}
								isLoading={isLoading}
								totalItems={totalItems}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<MetricDialog
				metric={editing}
				open={dialogOpen}
				onClose={() => {
					setDialogOpen(false);
					refetchData();
				}}
			/>

			<GenerateDialog
				kind="metrics"
				open={generateOpen}
				onClose={() => setGenerateOpen(false)}
				onSaved={() => refetchData()}
			/>

			<DeleteModal
				open={deleteOpen}
				onClose={closeDelete}
				onDelete={async () => {
					if (pendingDelete) await remove.mutateAsync(pendingDelete.id);
					closeDelete();
					refetchData();
				}}
				title="Delete metric"
				description="Results already scored against it stay readable."
			/>
		</React.Fragment>
	);
}
